import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { cn } from '../../utils/cn'
import {
  BarChart3,
  Server,
  RefreshCw,
  Settings,
  LogOut,
  Menu,
  X,
  Users,
  Globe,
  Shield,
  Zap,
} from 'lucide-react'
import { useAuth } from '../../hooks/useAuth'
import { useTheme } from '../../hooks/useTheme'

const navigationItems = [
  {
    name: 'Dashboard',
    href: '/',
    icon: BarChart3,
    description: 'Tổng quan hệ thống',
    color: 'text-blue-500',
  },
  {
    name: 'Providers',
    href: '/providers',
    icon: Users,
    description: 'Quản lý nhà cung cấp',
    color: 'text-green-500',
  },
  {
    name: 'Nodes',
    href: '/nodes',
    icon: Server,
    description: 'Xem và quản lý nodes',
    color: 'text-purple-500',
  },
  {
    name: 'Sync Logs',
    href: '/sync-logs',
    icon: RefreshCw,
    description: 'Lịch sử đồng bộ',
    color: 'text-orange-500',
  },
  {
    name: 'V2Board Panels',
    href: '/v2board-panels',
    icon: Shield,
    description: 'Panel và token đồng bộ',
    color: 'text-cyan-500',
  },
  {
    name: 'Settings',
    href: '/settings',
    icon: Settings,
    description: 'Cài đặt hệ thống',
    color: 'text-gray-500',
  },
]

interface SidebarProps {
  isOpen: boolean
  onClose: () => void
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const location = useLocation()
  const { user, logout } = useAuth()
  const { theme, toggleTheme } = useTheme()
  const { data: dashboardStats, isLoading: isStatsLoading, isError: isStatsError } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: async () => {
      const token = localStorage.getItem('auth_token')
      const response = await fetch('/api/v1/dashboard/stats', {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      if (!response.ok) throw new Error('Failed to fetch dashboard stats')
      return response.json()
    },
    refetchInterval: 30000,
  })

  const providerCount = dashboardStats?.providers?.total ?? 0
  const nodeCount = dashboardStats?.nodes?.total ?? 0
  const statusLabel = isStatsError ? 'Offline' : isStatsLoading ? 'Loading' : 'Active'

  return (
    <aside
      className={cn(
        'flex flex-col w-64 h-screen-mobile',
        'bg-surface-light dark:bg-surface-dark',
        'border-r border-secondary-200 dark:border-secondary-700',
        'transition-all duration-300 ease-smooth',
        'lg:relative lg:transform-none',
        isOpen ? 'translate-x-0' : '-translate-x-full',
      )}
    >
      {/* Logo and Header */}
      <div className="flex items-center justify-between p-6 border-b border-secondary-200 dark:border-secondary-700">
        <Link to="/" className="flex items-center gap-3" onClick={onClose}>
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center">
            <Globe className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="font-bold text-lg text-secondary-900 dark:text-white">
              V2Board Manager
            </h1>
            <p className="text-xs text-secondary-500 dark:text-secondary-400">
              Dashboard
            </p>
          </div>
        </Link>

        {/* Close button for mobile */}
        <button
          onClick={onClose}
          className="lg:hidden p-2 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-800 text-secondary-500 dark:text-secondary-400"
          aria-label="Close sidebar"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* User Profile */}
      <div className="p-6 border-b border-secondary-200 dark:border-secondary-700">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary-400 to-primary-500 flex items-center justify-center border-2 border-white dark:border-secondary-800">
            <span className="text-lg font-semibold text-white">
              {user?.username?.charAt(0).toUpperCase() || 'U'}
            </span>
          </div>
          <div className="flex-1">
            <p className="font-medium text-secondary-900 dark:text-white truncate">
              {user?.full_name || user?.username || 'User'}
            </p>
            <p className="text-sm text-secondary-500 dark:text-secondary-400 truncate">
              {user?.email || 'Administrator'}
            </p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-4 py-6">
        <div className="space-y-1">
          {navigationItems.map((item) => {
            const Icon = item.icon
            const isActive = location.pathname === item.href

            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={onClose}
                className={cn(
                  'flex items-center gap-3 px-3 py-3 rounded-lg',
                  'transition-all duration-200 ease-smooth',
                  isActive
                    ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
                    : 'hover:bg-secondary-100 dark:hover:bg-secondary-800 text-secondary-600 dark:text-secondary-400 hover:text-secondary-900 dark:hover:text-white',
                )}
              >
                <div
                  className={cn(
                    'p-2 rounded-lg',
                    isActive
                      ? 'bg-primary-100 dark:bg-primary-800'
                      : 'bg-secondary-100 dark:bg-secondary-800',
                  )}
                >
                  <Icon className={cn('w-5 h-5', item.color)} />
                </div>
                <div className="flex-1">
                  <p className={cn('font-medium', isActive && 'font-semibold')}>
                    {item.name}
                  </p>
                  <p className="text-xs mt-0.5 opacity-70">{item.description}</p>
                </div>
                {isActive && (
                  <div className="w-2 h-2 rounded-full bg-primary-500 animate-pulse" />
                )}
              </Link>
            )
          })}
        </div>

        {/* Quick Stats */}
        <div className="mt-8 p-4 rounded-xl bg-gradient-to-br from-primary-50 to-secondary-50 dark:from-primary-900/20 dark:to-secondary-900/20">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-secondary-700 dark:text-secondary-300">
              System Status
            </p>
            <div className="flex items-center">
              <div className={cn(
                'w-2 h-2 rounded-full mr-2',
                isStatsError ? 'bg-error-500' : isStatsLoading ? 'bg-warning-500' : 'bg-success-500 animate-pulse',
              )} />
              <span className={cn(
                'text-xs',
                isStatsError ? 'text-error-600 dark:text-error-400' : isStatsLoading ? 'text-warning-600 dark:text-warning-400' : 'text-success-600 dark:text-success-400',
              )}>
                {statusLabel}
              </span>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                {isStatsLoading ? '...' : providerCount}
              </p>
              <p className="text-xs text-secondary-500 dark:text-secondary-400">
                Providers
              </p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-success-600 dark:text-success-400">
                {isStatsLoading ? '...' : nodeCount}
              </p>
              <p className="text-xs text-secondary-500 dark:text-secondary-400">
                Nodes
              </p>
            </div>
          </div>
        </div>
      </nav>

      {/* Footer Actions */}
      <div className="border-t border-secondary-200 dark:border-secondary-700 p-4 space-y-2">
        {/* Theme Toggle */}
        <button
          onClick={toggleTheme}
          className="flex items-center justify-between w-full p-3 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-800 text-secondary-600 dark:text-secondary-400 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-secondary-100 dark:bg-secondary-800">
              {theme === 'dark' ? (
                <Sun className="w-4 h-4 text-yellow-500" />
              ) : (
                <Moon className="w-4 h-4 text-blue-500" />
              )}
            </div>
            <span className="font-medium">
              {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
            </span>
          </div>
          <div
            className={cn(
              'w-10 h-6 rounded-full relative transition-colors',
              theme === 'dark'
                ? 'bg-primary-500'
                : 'bg-secondary-300 dark:bg-secondary-600',
            )}
          >
            <div
              className={cn(
                'absolute top-1 w-4 h-4 rounded-full bg-white transition-transform',
                theme === 'dark' ? 'left-5' : 'left-1',
              )}
            />
          </div>
        </button>

        {/* Logout Button */}
        <button
          onClick={logout}
          className="flex items-center gap-3 w-full p-3 rounded-lg hover:bg-error-50 dark:hover:bg-error-900/30 text-error-600 dark:text-error-400 transition-colors"
        >
          <div className="p-2 rounded-lg bg-error-100 dark:bg-error-800">
            <LogOut className="w-4 h-4" />
          </div>
          <span className="font-medium">Đăng xuất</span>
        </button>
      </div>
    </aside>
  )
}

// Helper components
const Sun = ({ className }: { className?: string }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
    />
  </svg>
)

const Moon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
    />
  </svg>
)

export default Sidebar
